import { useEffect, useRef, useState, useCallback } from "react";
import { useContacts } from "../context/ContactsContext";
import { useTags } from "../context/TagsContext";
import { FaWhatsapp, FaPhone, FaEnvelope, FaInstagram } from "react-icons/fa";
import ContactInfo from "./ContactInfo";
import ContactSearchBar from "./ContactSearchBar";
import FilterModal from "./FilterModal";
import "./ContactsPage.css";

const methodIcons = {
  Whatsapp: <FaWhatsapp color="green" />,
  Telefon: <FaPhone color="blue" />,
  Email: <FaEnvelope color="orange" />,
  Instagram: <FaInstagram color="purple" />,
};

export default function ContactsPage() {
  
  const {
    contacts,
    loading,
    hasMore,
    refreshContacts,
    loadMore,
    selectedContactId,
    setSelectedContactId,
    owners: contextOwners,
    contactMethods: contextContactMethods,
    contactType: contextCustomerTypes,
    optionsLoading,
  } = useContacts();

  const { tags: contextTags } = useTags();

  const [filterOpen, setFilterOpen] = useState(false);
  const loadMoreLockRef = useRef(false);
  const [filters, setFilters] = useState(() => {
    // Aus LocalStorage laden
    const saved = localStorage.getItem("contactFilters");
    const initial = saved ? JSON.parse(saved) : {
      hasEmail: "both",
      hasPhone: "both",
      customerTypes: [],
      contactMethods: [],
      contactOwners: [],
      tags: [],
      hasEvent: "both",
      canContact: "both",
    };
    console.log("🔧 Initial filters state:", JSON.stringify(initial));
    return initial;
  });

  // Initial: ohne Suchbegriff laden
  useEffect(() => {
    console.log("🎯 EFFECT TRIGGERED - filters:", JSON.stringify(filters));
    refreshContacts({ q: "", filters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Sobald loading endet, Scroll-Load freigeben
  useEffect(() => {
    if (!loading) {
      loadMoreLockRef.current = false;
    }
  }, [loading]);

  // Debounce für serverseitige Suche (nur Q-Parameter, nicht Filter)
  const debounceRef = useRef(null);
  const handleSearch = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      refreshContacts({ q: value, filters });
    }, 250);
  };

  const handleApplyFilters = useCallback((newFilters) => {
    console.log("💥💥💥 handleApplyFilters - newFilters:", JSON.stringify(newFilters));
    setFilters(newFilters);
    localStorage.setItem("contactFilters", JSON.stringify(newFilters));
    setFilterOpen(false);
    // Direkt refreshContacts aufrufen wie beim loadMore()
    console.log("💥💥💥 Calling refreshContacts directly...");
    refreshContacts({ q: "", filters: newFilters });
  }, [refreshContacts]);

  const handleClearFilters = () => {
    const empty = {
      hasEmail: "both",
      hasPhone: "both",
      customerTypes: [],
      contactMethods: [],
      contactOwners: [],
      tags: [],
      hasEvent: "both",
      canContact: "both",
    };
    setFilters(empty);
    localStorage.setItem("contactFilters", JSON.stringify(empty));
  };

  // Auswahl aus der Typeahead-Liste: nur rechts öffnen, Liste unverändert lassen
  const handleSelectSuggestion = (contact) => {
    setSelectedContactId(contact.id);
  };

  // Hauptliste nutzt direkt die vom Server gelieferten Kontakte
  const visibleContacts = contacts;

  // Leere-State (falls geladen, aber keine Treffer)
  const isEmpty = !loading && visibleContacts.length === 0;

  return (
    <div className={`contacts-container ${selectedContactId ? "split" : ""}`}>
      <div className="contacts-list-panel">
        <div className="contacts-toolbar">
          <ContactSearchBar
            onSearch={handleSearch}
            onSelectSuggestion={handleSelectSuggestion}
            onFilter={handleApplyFilters}
            filters={filters}
          />
          <button 
            className="filter-button"
            onClick={() => setFilterOpen(true)}
            title="Filter öffnen"
          >
            🔍 Filter
          </button>
        </div>

        <FilterModal
          open={filterOpen}
          initial={filters}
          onClose={() => setFilterOpen(false)}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          customerTypes={contextCustomerTypes}
          contactMethods={contextContactMethods}
          owners={contextOwners}
          allTags={contextTags}
        />

        <div
          className="table-wrapper"
          onScroll={(e) => {
            const target = e.currentTarget;
            if (
              target.scrollTop + target.clientHeight >= target.scrollHeight - 200 &&
              hasMore &&
              !loading &&
              !loadMoreLockRef.current
            ) {
              loadMoreLockRef.current = true;
              loadMore();
            }
          }}
        >
          {isEmpty ? (
            <p className="empty-state">Keine Treffer</p>
          ) : (
            <table className="contacts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Firma</th>
                  <th>Methode</th>
                  <th>Event</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                {visibleContacts.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedContactId(c.id)}
                    className={`${selectedContactId === c.id ? "selected" : ""} ${
                      c.canContact === false ? "cannot-contact" : ""
                    }`}
                  >
                    <td>{c.name}</td>
                    <td>{c.company || "-"}</td>
                    <td>
                      {c.contactMethod
                        ? methodIcons[c.contactMethod] || c.contactMethod
                        : "-"}
                    </td>
                    <td>{c.eventDay ? `${c.eventDay}` : "-"}</td>
                    <td>{c.ownerUsername || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {loading && <p>⏳ Lade mehr...</p>}
          {!loading && !hasMore && !isEmpty && <p>✅ Alle Kontakte geladen</p>}
        </div>
      </div>

      {selectedContactId && (
        <div className="contact-info-panel">
          <ContactInfo contactId={selectedContactId} />
        </div>
      )}
    </div>
  );
}
